import { Dialog, Menu, Transition } from '@headlessui/react';
import { useState } from 'react';
import {
  QuestionMarkCircleIcon,
  UserIcon,
  FolderIcon,
  HomeIcon,
  TagIcon,
  CodeIcon,
  MenuAlt2Icon,
  UsersIcon,
  XIcon,
  CollectionIcon,
  CurrencyDollarIcon,
  ClipboardListIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/outline';
import clsx from 'clsx';
import * as React from 'react';

import { useAuth } from '@/lib/auth';
import { useAuthorization, ROLES } from '@/lib/authorization';
import logo from '@/assets/CSP-logo-white.png';
import logoCut from '@/assets/logo/LogoCut.png';
import { NavLink, Link } from 'react-router-dom';

type SideNavigationItem = {
  name: string;
  to: string;
  icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  children?: SideNavigationItem[];
};

const SideNavigation = ({ collapsed = false }: { collapsed?: boolean }) => {
  const { checkAccess } = useAuthorization();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const toggleMenu = (name: string) => {
    setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));
  };
  const navigation = [
    { name: 'Dashboard', to: '.', icon: HomeIcon },
    { name: 'Welcome Video', to: './intro', icon: HomeIcon },
    { name: 'Version Management', to: './versionManage', icon: CodeIcon },
    checkAccess({ allowedRoles: [ROLES.ADMIN] }) && {
      name: 'Users',
      to: './users',
      icon: UsersIcon,
    },
    { name: 'Intake Quiz', to: './quiz', icon: QuestionMarkCircleIcon },
    { name: 'Tags', to: './tags', icon: TagIcon },
    { name: 'Categories', to: './categories', icon: ClipboardListIcon },
    { name: 'Collections', to: './collections', icon: CollectionIcon },
    { name: 'Exercises', to: './exercises', icon: FolderIcon },
    {
      name: 'Equipment', to: './equipment', icon: CurrencyDollarIcon, children: [
        { name: 'Shop Link', to: './equipment/shop-link' },],
    },
  ].filter(Boolean) as SideNavigationItem[];

  return (
    <>
      {navigation.map((item, index) =>
        item.children ? (
          <div key={item.name} className="mb-2">
            <div className={clsx('flex items-center w-full', collapsed ? 'justify-center' : 'justify-between')}>
              <NavLink
                end={index === 0}
                to={item.to!}
                title={item.name}
                className={({ isActive }) =>
                  clsx(
                    'group flex items-center px-2 py-2 text-base font-medium rounded-md',
                    collapsed ? 'justify-center w-12 h-12' : 'flex-1',
                    isActive
                      ? 'bg-red-600 text-white'
                      : 'text-gray-300 hover:bg-[#333] hover:text-white'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {item.icon && (
                      <item.icon
                        className={clsx(
                          'flex-shrink-0 h-6 w-6',
                          !collapsed && 'mr-4',
                          isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
                        )}
                      />
                    )}
                    {!collapsed && item.name}
                  </>
                )}
              </NavLink>
              {!collapsed && (
                <button
                  onClick={() => toggleMenu(item.name)}
                  className="p-2 focus:outline-none"
                >
                  {openMenus[item.name] ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              )}
            </div>
            {!collapsed && openMenus[item.name] && (
                <div className="mt-1 space-y-1 pl-6">
                {item.children.map((child) => (
                  <NavLink
                    key={child.name}
                    to={child.to!}
                    className={({ isActive }) =>
                      clsx(
                        'group flex items-center px-2 py-2 text-sm rounded-md',
                        isActive
                          ? 'bg-red-600 text-white'
                          : 'text-gray-300 hover:bg-[#333] hover:text-white'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {child.icon && (
                          <child.icon
                            className={clsx(
                              'mr-3 h-5 w-5 flex-shrink-0',
                              isActive
                                ? 'text-white'
                                : 'text-gray-400 group-hover:text-gray-300'
                            )}
                          />
                        )}
                        {child.name}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ) : (
          <NavLink
            end={index === 0}
            key={item.name}
            to={item.to!}
            title={item.name}
            className={({ isActive }) =>
              clsx(
                'group flex items-center px-2 py-2 text-base font-medium rounded-md',
                collapsed ? 'justify-center w-12 h-12 mx-auto' : '',
                isActive
                  ? 'bg-red-600 text-white'
                  : 'text-gray-300 hover:bg-[#333] hover:text-white'
              )
            }
          >
            {({ isActive }) => (
              <>
                {item.icon && (
                  <item.icon
                    className={clsx(
                      'flex-shrink-0 h-6 w-6',
                      !collapsed && 'mr-4',
                      isActive
                        ? 'text-white'
                        : 'text-gray-400 group-hover:text-gray-300'
                    )}
                  />
                )}
                {!collapsed && item.name}
              </>
            )}
          </NavLink>
        )
      )}
    </>
  );
};


type UserNavigationItem = {
  name: string;
  to: string;
  onClick?: () => void;
};

const UserNavigation = () => {
  const { signOut } = useAuth();

  const userNavigation = [
    { name: 'Your Profile', to: './profile' },
    {
      name: 'Sign out',
      to: '/',
      onClick: () => {
        signOut();
      },
    },
  ].filter(Boolean) as UserNavigationItem[];

  return (
    <Menu as="div" className="ml-3 relative">
      {({ open }) => (
        <>
          <div>
            <Menu.Button className="max-w-xs  bg-gray-200 p-2 flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              <span className="sr-only">Open user menu</span>
              <UserIcon className="h-8 w-8 rounded-full" />
            </Menu.Button>
          </div>
          <Transition
            show={open}
            as={React.Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              static
              className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              {userNavigation.map((item) => (
                <Menu.Item key={item.name}>
                  {({ active }) => (
                    <Link
                      onClick={item.onClick}
                      to={item.to}
                      className={clsx(
                        active ? 'bg-gray-100' : '',
                        'block px-4 py-2 text-sm text-gray-700'
                      )}
                    >
                      {item.name}
                    </Link>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

type MobileSidebarProps = {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const MobileSidebar = ({ sidebarOpen, setSidebarOpen }: MobileSidebarProps) => {
  return (
    <Transition.Root show={sidebarOpen} as={React.Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 flex z-40 md:hidden"
        open={sidebarOpen}
        onClose={setSidebarOpen}
      >
        <Transition.Child
          as={React.Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </Transition.Child>
        <Transition.Child
          as={React.Fragment}
          enter="transition ease-in-out duration-300 transform"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-gray-800">
            <Transition.Child
              as={React.Fragment}
              enter="ease-in-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 flex items-center px-4">
              <Logo />
            </div>
            <div className="mt-5 flex-1 h-0 overflow-y-auto">
              <nav className="px-2 space-y-1">
                <SideNavigation />
              </nav>
            </div>
          </div>
        </Transition.Child>
        <div className="flex-shrink-0 w-14" aria-hidden="true"></div>
      </Dialog>
    </Transition.Root>
  );
};

const Sidebar = ({
  collapsed,
  onToggleCollapse,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
}) => {
  return (
    <div className="hidden md:flex md:flex-shrink-0">
      <div className={clsx('flex flex-col transition-all duration-200', collapsed ? 'w-20' : 'w-64')}>
        <div className="flex flex-col h-0 flex-1">
          <div
            className={clsx(
              'flex items-center h-16 flex-shrink-0 bg-black border-b border-gray-800',
              collapsed ? 'justify-center px-2' : 'justify-start px-4'
            )}
          >
            <Logo collapsed={collapsed} />
          </div>
          <div className="flex-1 flex flex-col overflow-y-auto">
            <nav className={clsx('flex-1 py-4 bg-[#0d0d0d] space-y-1', collapsed ? 'px-1' : 'px-2')}>
              <SideNavigation collapsed={collapsed} />
            </nav>
          </div>
          <div className="bg-[#0d0d0d] border-t border-gray-800 p-2">
            <button
              type="button"
              onClick={onToggleCollapse}
              className={clsx(
                'w-full rounded-md text-gray-300 hover:text-white hover:bg-[#333] focus:outline-none flex items-center',
                collapsed ? 'justify-center px-2 py-3' : 'justify-between px-3 py-2'
              )}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <span className={clsx('text-sm font-medium', collapsed && 'hidden')}>Collapse Sidebar</span>
              {collapsed ? (
                <ChevronRightIcon className="h-5 w-5" />
              ) : (
                <ChevronLeftIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Logo = ({ collapsed = false }: { collapsed?: boolean }) => {
  return (
    <Link className={clsx('flex items-center text-white', collapsed ? 'py-2' : 'm-5')} to=".">
      {collapsed ? (
        <div className="w-14 h-12 overflow-hidden rounded bg-black flex items-center justify-center">
          <img
            src={logoCut}
            alt="CSP logo"
            className="h-10 w-10 object-contain"
          />
        </div>
      ) : (
        <img src={logo} alt="CSP logo" />
      )}
    </Link>
  );
};

export const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      <MobileSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Sidebar
        collapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
      />
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="flex-1 px-4 flex justify-end">
            <div className="ml-4 flex items-center md:ml-6">
              <UserNavigation />
            </div>
          </div>
        </div>
        <main className="flex-1 relative overflow-y-auto focus:outline-none">{children}</main>
      </div>
    </div>
  );
};